import React from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';

const debounceDelay = 300;
const defaultRowsPerPage = 25;

const TABLE_COLUMNS = [
  { key: 'select', label: '', minWidth: 56, maxWidth: 90, defaultWidth: 56, resizable: false },
  { key: 'preview', label: 'Mockup', minWidth: 96, maxWidth: 160, defaultWidth: 112 },
  { key: 'listing', label: 'Listing', minWidth: 260, maxWidth: 560, defaultWidth: 340 },
  { key: 'image', label: 'Image', minWidth: 220, maxWidth: 520, defaultWidth: 300 },
  { key: 'optimized', label: 'Optimized', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'position', label: 'Position', minWidth: 100, maxWidth: 180, defaultWidth: 110 },
  { key: 'status', label: 'Listing Status', minWidth: 130, maxWidth: 240, defaultWidth: 150 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 340, defaultWidth: 210 },
  { key: 'source', label: 'Source', minWidth: 96, maxWidth: 180, defaultWidth: 110 },
];

const optimizedOptions = [
  { value: '', label: 'All mockups' },
  { value: 'unoptimized', label: 'Unoptimized' },
  { value: 'optimized', label: 'Optimized' },
];

function formatDate(value) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
}

function imageUrl(item) {
  return item?.url || item?.src || '';
}

function imageName(item) {
  if (item?.name) {
    return item.name;
  }
  const url = imageUrl(item);
  if (!url) {
    return '—';
  }
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    return segments[segments.length - 1] || url;
  } catch {
    const segments = url.split('/').filter(Boolean);
    return segments[segments.length - 1] || url;
  }
}

const WooCommerceMockupsPage = () => {
  const { fetchWithAuth } = useAuth();
  const { latestJobUpdate } = useRealtime();
  const [sites, setSites] = React.useState([]);
  const [sitesLoading, setSitesLoading] = React.useState(false);
  const [selectedSiteId, setSelectedSiteId] = React.useState('');
  const [selectedOptimized, setSelectedOptimized] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [optimizing, setOptimizing] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [actionMessage, setActionMessage] = React.useState(null);
  const refreshTimerRef = React.useRef(null);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.woocommerce-mockups',
  });

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  React.useEffect(() => {
    setPage(0);
  }, [debouncedSearch, rowsPerPage, selectedSiteId, selectedOptimized]);

  const loadSites = React.useCallback(async () => {
    setSitesLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/sites`);
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load WooCommerce sites');
      }
      const payload = await resp.json();
      const nextSites = Array.isArray(payload.items) ? payload.items : [];
      setSites(nextSites);
      setSelectedSiteId((current) => (
        current && nextSites.some((site) => site.id === current) ? current : nextSites[0]?.id || ''
      ));
    } catch (err) {
      setError(err.message || 'Failed to load WooCommerce sites');
    } finally {
      setSitesLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadSites();
  }, [loadSites]);

  const loadMockups = React.useCallback(async () => {
    if (!selectedSiteId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('site_id', selectedSiteId);
      params.set('limit', rowsPerPage.toString());
      params.set('offset', (page * rowsPerPage).toString());
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      if (selectedOptimized) {
        params.set('optimized', selectedOptimized);
      }
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/mockups?${params.toString()}`);
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load WooCommerce mockups');
      }
      const payload = await resp.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch (err) {
      setError(err.message || 'Failed to load WooCommerce mockups');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, fetchWithAuth, page, rowsPerPage, selectedOptimized, selectedSiteId]);

  React.useEffect(() => {
    loadMockups();
  }, [loadMockups]);

  React.useEffect(() => {
    setSelectedIds(new Set());
  }, [items, selectedSiteId, selectedOptimized]);

  React.useEffect(() => {
    if (!latestJobUpdate || latestJobUpdate.type !== 'job.updated') {
      return undefined;
    }
    const job = latestJobUpdate.job;
    if (!job || job.name !== 'OptimizeWooCommerceMockups') {
      return undefined;
    }
    const status = Number(job.status);
    if (status !== 2 && status !== 3) {
      return undefined;
    }
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => {
      loadMockups();
    }, 500);
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
      }
    };
  }, [latestJobUpdate, loadMockups]);

  const visibleImageIds = React.useMemo(
    () => items.map((item) => item.id).filter(Boolean),
    [items],
  );
  const selectedCount = selectedIds.size;
  const visibleSelectedCount = React.useMemo(
    () => visibleImageIds.filter((id) => selectedIds.has(id)).length,
    [selectedIds, visibleImageIds],
  );
  const allSelected = visibleImageIds.length > 0 && visibleSelectedCount === visibleImageIds.length;
  const indeterminate = visibleSelectedCount > 0 && visibleSelectedCount < visibleImageIds.length;
  const actionsOpen = Boolean(actionsAnchorEl);
  const actionsDisabled = !selectedSiteId || selectedCount === 0 || optimizing;

  const handleToggleAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(new Set(visibleImageIds));
      return;
    }
    setSelectedIds(new Set());
  };

  const handleToggleRow = (id) => {
    if (!id) {
      return;
    }
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const closeActionsMenu = () => {
    setActionsAnchorEl(null);
  };

  const handleOptimizeSelected = async () => {
    if (!selectedSiteId || selectedIds.size === 0) {
      closeActionsMenu();
      return;
    }
    setOptimizing(true);
    setError(null);
    setActionMessage(null);
    closeActionsMenu();
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/mockups/optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSiteId,
          image_ids: Array.from(selectedIds),
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to queue mockup optimization');
      }
      const queued = typeof payload.queued === 'number' ? payload.queued : selectedIds.size;
      setSelectedIds(new Set());
      setActionMessage(`Queued ${queued} mockup${queued === 1 ? '' : 's'} for WebP optimization.`);
    } catch (err) {
      setError(err.message || 'Failed to queue mockup optimization');
    } finally {
      setOptimizing(false);
    }
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
        <Box sx={{ minWidth: { lg: 260 } }}>
          <Typography variant="h5">WooCommerce Mockups</Typography>
          <Typography variant="body2" color="text.secondary">
            {total} image{total === 1 ? '' : 's'}
          </Typography>
        </Box>
        <TextField
          select
          label="WooCommerce Site"
          value={selectedSiteId}
          onChange={(event) => setSelectedSiteId(event.target.value)}
          sx={{ minWidth: 260 }}
          disabled={sitesLoading || sites.length === 0}
        >
          {sites.length === 0 ? (
            <MenuItem value="" disabled>
              {sitesLoading ? 'Loading...' : 'No sites'}
            </MenuItem>
          ) : (
            sites.map((site) => (
              <MenuItem key={site.id} value={site.id}>
                {site.name || site.url}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          fullWidth
          placeholder="Search listings, images, or media IDs"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <TextField
          select
          label="Optimized"
          value={selectedOptimized}
          onChange={(event) => setSelectedOptimized(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          {optimizedOptions.map((option) => (
            <MenuItem key={option.value || 'all'} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          endIcon={optimizing ? <CircularProgress color="inherit" size={16} /> : <KeyboardArrowDownIcon />}
          onClick={(event) => setActionsAnchorEl(event.currentTarget)}
          disabled={actionsDisabled}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Actions
        </Button>
        <Menu anchorEl={actionsAnchorEl} open={actionsOpen} onClose={closeActionsMenu}>
          <MenuItem onClick={handleOptimizeSelected} disabled={optimizing || selectedCount === 0}>
            Optimize
          </MenuItem>
        </Menu>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {actionMessage ? <Alert severity="success">{actionMessage}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  column.key === 'select' ? (
                    <TableCell key={column.key} padding="checkbox" sx={{ width: getWidth(column.key) }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={indeterminate}
                        onChange={handleToggleAll}
                        inputProps={{ 'aria-label': 'Select all mockups on this page' }}
                      />
                    </TableCell>
                  ) : (
                    <ResizableHeaderCell
                      key={column.key}
                      column={column}
                      getWidth={getWidth}
                      onResizeStart={startColumnResize}
                    />
                  )
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading WooCommerce mockups..." />
              ) : items.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No mockups found." />
              ) : (
                items.map((item) => {
                  const selected = selectedIds.has(item.id);
                  const url = imageUrl(item);
                  return (
                    <TableRow key={item.id} hover selected={selected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected}
                          onChange={() => handleToggleRow(item.id)}
                          inputProps={{ 'aria-label': `Select ${imageName(item)}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Avatar
                          src={url}
                          alt={item.alt || imageName(item)}
                          variant="rounded"
                          sx={{ width: 64, height: 64 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" noWrap title={item.listing_name || item.listing_title}>
                          {item.listing_name || item.listing_title || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {item.listing_external_id || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={imageName(item)}>
                          {imageName(item)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Media ID: {item.external_id || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.optimized ? 'Optimized' : 'Unoptimized'}
                          color={item.optimized ? 'success' : 'warning'}
                          variant={item.optimized ? 'filled' : 'outlined'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{item.rank || item.position + 1 || '—'}</TableCell>
                      <TableCell>
                        <Chip label={item.listing_status || '—'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{formatDate(item.updated_at)}</TableCell>
                      <TableCell>
                        {url ? (
                          <Tooltip title="Open source image">
                            <IconButton component="a" href={url} target="_blank" rel="noopener noreferrer" size="small">
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default WooCommerceMockupsPage;
